'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Props interface for the TextPressure component.
 * Defines all configurable options for the interactive text effect.
 */
interface TextPressureProps {
    text?: string;                 // Text to display and animate
    fontFamily?: string;           // Font family to use
    fontUrl?: string;              // URL to the variable font file
    weight?: boolean;              // Whether to animate font weight
    italic?: boolean;              // Whether to animate italic style
    alpha?: boolean;               // Whether to animate opacity
    stroke?: boolean;              // Whether to apply stroke effect
    scale?: boolean;               // Whether to scale text to fit container
    textColor?: string;            // Color of the text
    strokeColor?: string;          // Color of the stroke (if enabled)
    strokeWidth?: number;          // Width of the stroke (if enabled)
    className?: string;            // Additional CSS classes
    minFontSize?: number;          // Minimum font size in pixels
    divTag?: boolean;              // Whether to use a div tag instead of h1
}

/**
 * TextPressure - An interactive text component that responds to cursor proximity
 * by animating variable font parameters (weight, italic) and other properties.
 * 
 * The component creates an engaging typographic effect where letters respond
 * to cursor movement, becoming bolder and potentially changing style as the
 * cursor approaches each character.
 */
const TextPressure: React.FC<TextPressureProps> = ({
    text = 'Valdesio',
    fontFamily = 'Inter',
    fontUrl = '/fonts/Inter-VariableFont_opsz,wght.woff2',
    weight = true,
    italic = true,
    alpha = false,
    stroke = false,
    scale = false,
    textColor = '#FFFFFF',
    strokeColor = '#FF0000',
    strokeWidth = 2,
    className = '',
    minFontSize = 24,
    divTag = false,
}) => {
    // Refs to access DOM elements
    const containerRef = useRef<HTMLDivElement | null>(null);        // Container element
    const titleRef = useRef<HTMLHeadingElement | null>(null);        // Title element
    const spansRef = useRef<(HTMLSpanElement | null)[]>([]);         // Individual character spans

    // Cursor position tracking
    const mouseRef = useRef({ x: 0, y: 0 });      // Smoothed cursor position (lags behind actual cursor)
    const cursorRef = useRef({ x: 0, y: 0 });     // Actual cursor position
    
    // Animation frame tracking for proper cleanup
    const animationFrameRef = useRef<number | null>(null);
    const isAnimatingRef = useRef(false);

    // State for text sizing and scaling
    const [fontSize, setFontSize] = useState(minFontSize);   // Calculated font size
    const [scaleY, setScaleY] = useState(1);                 // Vertical scale factor
    const [lineHeight, setLineHeight] = useState(1);         // Line height

    // State for responsive screen size detection
    const [isDesktop, setIsDesktop] = useState(false);       // Track if screen is desktop size (>= 992px)

    // Split text into individual characters for animation
    const chars = text.split('');

    /**
     * Calculate Euclidean distance between two points
     * Used to determine cursor proximity to each character
     */
    const dist = (a: { x: number; y: number }, b: { x: number; y: number }) => {
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        return Math.sqrt(dx * dx + dy * dy);
    };

    /**
     * Set up event listeners for cursor movement (desktop only)
     * Tracks both mouse and touch interactions
     */
    useEffect(() => {
        // Only set up animation event listeners on desktop
        if (!isDesktop) return;

        // Update cursor position on mouse move
        const handleMouseMove = (e: MouseEvent) => {
            cursorRef.current.x = e.clientX;
            cursorRef.current.y = e.clientY;
        };

        // Update cursor position on touch move
        const handleTouchMove = (e: TouchEvent) => {
            const t = e.touches[0];
            cursorRef.current.x = t.clientX;
            cursorRef.current.y = t.clientY;
        };

        // Add event listeners
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('touchmove', handleTouchMove, { passive: false });

        // Initialize cursor position to center of container
        if (containerRef.current) {
            const { left, top, width, height } = containerRef.current.getBoundingClientRect();
            mouseRef.current.x = left + width / 2;
            mouseRef.current.y = top + height / 2;
            cursorRef.current.x = mouseRef.current.x;
            cursorRef.current.y = mouseRef.current.y;
        }

        // Clean up event listeners on unmount
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('touchmove', handleTouchMove);
        };
    }, [isDesktop]);

    /**
     * Calculate appropriate font size and scaling based on container dimensions
     * Ensures text fits nicely within its container
     */
    const setSize = () => {
        if (!containerRef.current || !titleRef.current) return;

        const { width: containerW, height: containerH } = containerRef.current.getBoundingClientRect();

        // Calculate font size based on container width and character count
        // The factor 1.4 is a heuristic to balance character width and spacing
        let newFontSize = containerW / (chars.length / 1.4);
        newFontSize = Math.max(newFontSize, minFontSize);

        // Update state with new size values
        setFontSize(newFontSize);
        setScaleY(1);
        setLineHeight(1);

        // After render, check if vertical scaling is needed
        requestAnimationFrame(() => {
            if (!titleRef.current) return;
            const textRect = titleRef.current.getBoundingClientRect();

            // If scale option is enabled and text has height, adjust vertical scaling
            if (scale && textRect.height > 0) {
                const yRatio = containerH / textRect.height;
                setScaleY(yRatio);
                setLineHeight(yRatio);
            }
        });
    };

    /**
     * Set up resize handling and initial sizing
     * Recalculates text size whenever the window resizes or text changes
     */
    useEffect(() => {
        setSize();
        window.addEventListener('resize', setSize);
        return () => window.removeEventListener('resize', setSize);
    }, [scale, text]);

    /**
     * Set up responsive screen size detection
     * Tracks whether the screen is desktop size (>= 992px) for conditional rendering
     * This enables the component to switch between desktop animation and mobile static rendering
     */
    useEffect(() => {
        const handleResize = () => {
            setIsDesktop(window.innerWidth >= 992);
        };

        // Set initial value
        handleResize();

        // Add resize event listener
        window.addEventListener('resize', handleResize);

        // Clean up event listener on component unmount
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    /**
     * Component cleanup effect
     * Ensures all animation resources are properly cleaned up when component unmounts
     */
    useEffect(() => {
        return () => {
            // Stop all animations
            isAnimatingRef.current = false;
            
            // Cancel any pending animation frames
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
            
            // Reset all character styles to prevent memory leaks
            spansRef.current.forEach((span) => {
                if (!span) return;
                span.style.opacity = '';
                span.style.removeProperty('--wght');
                span.style.removeProperty('--ital');
                span.style.fontVariationSettings = '';
                span.style.transition = '';
            });
        };
    }, []);

    /**
     * Main animation loop (desktop only)
     * Updates character styles based on cursor proximity
     */
    useEffect(() => {
        // Cancel any existing animation frame before starting new one
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
        isAnimatingRef.current = false;

        // Only run animation loop on desktop
        if (!isDesktop) {
            // Reset all character styles to default when switching to mobile
            spansRef.current.forEach((span) => {
                if (!span) return;
                span.style.opacity = '1';
                span.style.removeProperty('--wght');
                span.style.removeProperty('--ital');
                span.style.fontVariationSettings = '';
                span.style.transition = '';
            });
            return;
        }

        // Set animation flag to true for desktop mode
        isAnimatingRef.current = true;

        const animate = () => {
            // Check if we should continue animating (prevents race conditions)
            if (!isAnimatingRef.current) return;

            // Smooth cursor movement by interpolating towards actual cursor position
            // This creates a more natural, fluid motion with easing
            mouseRef.current.x += (cursorRef.current.x - mouseRef.current.x) / 15;
            mouseRef.current.y += (cursorRef.current.y - mouseRef.current.y) / 15;

            if (titleRef.current) {
                const titleRect = titleRef.current.getBoundingClientRect();
                const maxDist = titleRect.width / 2;  // Maximum distance to consider for effect

                // Update each character span
                spansRef.current.forEach((span) => {
                    if (!span || !isAnimatingRef.current) return;

                    // Get character position
                    const rect = span.getBoundingClientRect();
                    const charCenter = {
                        x: rect.x + rect.width / 2,
                        y: rect.y + rect.height / 2,
                    };

                    // Calculate distance between cursor and character
                    const d = dist(mouseRef.current, charCenter);

                    // Normalize distance to create a 0-1 value
                    // Closer to character = closer to 0
                    const normalizedDist = Math.min(1, d / maxDist);

                    // Invert the normalized distance to get effect strength
                    // Closer to character = higher value (closer to 1)
                    const effectStrength = 1 - normalizedDist;

                    // Calculate font weight based on proximity
                    // Closer = bolder (higher weight value)
                    const wghtMin = 100;  // Minimum weight (thinnest)
                    const wghtMax = 900;  // Maximum weight (boldest)
                    const wghtTarget = weight
                        ? Math.floor(wghtMin + (wghtMax - wghtMin) * effectStrength)
                        : 400;  // If weight animation disabled, use constant weight

                    // Calculate italic value based on proximity (0-1 range)
                    const italTarget = italic ? effectStrength.toFixed(2) : '0';

                    // Calculate opacity based on proximity (0-1 range)
                    const alphaVal = alpha ? effectStrength.toFixed(2) : '1';

                    // Smooth transitions by interpolating current values toward target values
                    // This prevents sudden jumps and creates more natural animation
                    const prevWght = parseFloat(span.style.getPropertyValue('--wght')) || 400;
                    const prevItal = parseFloat(span.style.getPropertyValue('--ital')) || 0;

                    // Apply 20% of the difference each frame for smooth interpolation
                    const smoothWght = prevWght + (wghtTarget - prevWght) * 0.2;
                    const smoothItal = prevItal + (parseFloat(italTarget) - prevItal) * 0.2;

                    // Apply calculated values to the span
                    span.style.opacity = alphaVal;
                    span.style.setProperty('--wght', smoothWght.toString());
                    span.style.setProperty('--ital', smoothItal.toString());
                    span.style.fontVariationSettings = `'wght' ${smoothWght}, 'ital' ${smoothItal}`;

                    // Ensure smooth animation with CSS transition
                    span.style.transition = 'font-variation-settings 0.2s ease-out';
                });
            }

            // Continue animation loop only if still animating
            if (isAnimatingRef.current) {
                animationFrameRef.current = requestAnimationFrame(animate);
            }
        };

        // Start animation loop
        animationFrameRef.current = requestAnimationFrame(animate);

        // Clean up animation on unmount or when switching to mobile
        return () => {
            isAnimatingRef.current = false;
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        };
    }, [isDesktop, weight, italic, alpha, chars.length]);

    // Render title with either div or h1 based on divTag prop
    const TitleTag = divTag ? 'div' : 'h1';

    /**
     * Renders the desktop version with full animation functionality
     * Each character is wrapped in a span for individual animation
     */
    const renderDesktopVersion = () => (
        <TitleTag
            ref={titleRef}
            className={`text-pressure-title ${className} ${stroke ? 'stroke' : ''} uppercase text-center`}
            style={{
                fontFamily,
                fontSize,
                lineHeight,
                transform: `scale(1, ${scaleY})`,  // Apply vertical scaling if needed
                transformOrigin: 'center top',     // Scale from the top center
                fontWeight: 400,                   // Use a neutral base weight
                color: stroke ? undefined : textColor,
            }}
        >
            {/* Map each character to an individually animated span */}
            {chars.map((char, i) => (
                <span
                    key={i}
                    ref={(el) => { spansRef.current[i] = el }}
                    data-char={char}
                    className="inline-block"
                >
                    {char}
                </span>
            ))}
        </TitleTag>
    );

    /**
     * Renders the mobile version with static text (no animation spans)
     * Uses the same styling, font sizing, and scaling logic as desktop
     */
    const renderMobileVersion = () => (
        <TitleTag
            ref={titleRef}
            className={`text-pressure-title ${className} ${stroke ? 'stroke' : ''} uppercase text-center`}
            style={{
                fontFamily,
                fontSize,
                lineHeight,
                transform: `scale(1, ${scaleY})`,  // Apply vertical scaling if needed
                transformOrigin: 'center top',     // Scale from the top center
                fontWeight: 400,                   // Use a neutral base weight
                color: stroke ? undefined : textColor,
            }}
        >
            {text}
        </TitleTag>
    );

    return (
        <div ref={containerRef} className="relative w-full h-full overflow-hidden bg-transparent flex items-center justify-center">
            {/* Inline styles for font face and stroke effect */}
            <style>{`
                /* Define custom font face with variable font file */
                @font-face {
                    font-family: '${fontFamily}';
                    src: url('${fontUrl}');
                    font-style: normal;
                }
                /* Styles for stroke effect if enabled */
                .stroke span {
                    position: relative;
                    color: ${textColor};
                }
                .stroke span::after {
                    content: attr(data-char);
                    position: absolute;
                    left: 0;
                    top: 0;
                    color: transparent;
                    z-index: -1;
                    -webkit-text-stroke-width: ${strokeWidth}px;
                    -webkit-text-stroke-color: ${strokeColor};
                }
            `}</style>

            {/* Conditionally render desktop or mobile version based on screen size */}
            {isDesktop ? renderDesktopVersion() : renderMobileVersion()}
        </div>
    );
};

export default TextPressure;